// Shared fetch wrapper.
//
// - `credentials: 'include'` so the browser sends the session cookie.
// - On state-changing methods (POST/PUT/PATCH/DELETE) we attach the CSRF token
//   from the auth store as `X-CSRF-Token`. The backend's CSRF middleware
//   compares it to the per-session token stored server-side.
//
// Errors are thrown as ApiError so views can show them.
import { useAuthStore } from '@/shared/stores/auth'

export class ApiError extends Error {
    status: number
    body: unknown
    constructor(status: number, body: unknown, message: string) {
        super(message)
        this.status = status
        this.body = body
    }
}

const STATE_CHANGING = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

export interface RequestOpts extends RequestInit {
    json?: unknown
}

export async function request<T = unknown>(path: string, opts: RequestOpts = {}): Promise<T> {
    const method = (opts.method ?? 'GET').toUpperCase()
    const headers = new Headers(opts.headers ?? {})
    let body = opts.body

    if (opts.json !== undefined) {
        headers.set('content-type', 'application/json')
        body = JSON.stringify(opts.json)
    }

    if (STATE_CHANGING.has(method)) {
        const auth = useAuthStore()
        if (auth.csrfToken) {
            headers.set('x-csrf-token', auth.csrfToken)
        }
    }

    const res = await fetch(path, {
        ...opts,
        method,
        headers,
        body,
        credentials: 'include',
    })

    const text = await res.text()
    const parsed = text ? safeJson(text) : null
    if (!res.ok) {
        // A 401 while we believed we were signed in means the session died
        // out from under us (idle timeout, revocation). Clear local auth and
        // send the user to reauthenticate — except for /api/me itself, whose
        // 401 is how the auth store discovers "not signed in" on public pages.
        if (res.status === 401 && !path.startsWith('/api/me')) {
            const auth = useAuthStore()
            if (auth.user) {
                auth.user = null
                window.location.assign('/login?reason=expired')
            }
        }
        const message =
            (parsed && typeof parsed === 'object' && 'error' in parsed
                ? String((parsed as { error: unknown }).error)
                : null) ?? `${res.status} ${res.statusText}`
        throw new ApiError(res.status, parsed, message)
    }
    return parsed as T
}

function safeJson(text: string): unknown {
    try {
        return JSON.parse(text)
    } catch {
        return text
    }
}
